import {HttpClient} from "@angular/common/http";
import {datadogRum} from "@datadog/browser-rum";
import {Injectable, signal} from "@angular/core";
import {Observable, catchError, map, of, tap} from "rxjs";

import {Response} from "./local/Response";

@Injectable({providedIn: "root"})
export class TenantFeatureService {
  private readonly featuresState = signal<Record<string, boolean>>({});
  private readonly loadedState = signal(false);
  /** True after the last load() attempt failed — distinguishes "flag off" from "flags unknown". */
  private readonly loadFailedState = signal(false);

  readonly features = this.featuresState.asReadonly();
  readonly loaded = this.loadedState.asReadonly();
  readonly loadFailed = this.loadFailedState.asReadonly();

  constructor(private readonly httpClient: HttpClient) {}

  load(): Observable<Record<string, boolean>> {
    if (this.loadedState()) {
      return of(this.featuresState());
    }

    return this.httpClient.get<Response<Record<string, boolean>>>("tenant/features").pipe(
      map((response) => response.data),
      tap((features) => {
        this.featuresState.set(features);
        this.loadedState.set(true);
        this.loadFailedState.set(false);
      }),
      catchError(() => {
        // Keep loaded=false after a transient failure so a later call can retry.
        this.loadFailedState.set(true);
        return of({});
      }),
    );
  }

  isEnabled(feature: string): boolean {
    return this.featuresState()[feature] ?? false;
  }

  /**
   * A failed feature call makes every flag look "off", so a correctly configured
   * widget silently renders its legacy view. Record that in RUM so the fallback
   * is visible in Datadog instead of looking like a misconfigured code.
   */
  reportUnavailable(context: string): void {
    if (!this.loadFailedState()) return;
    console.warn(`[TenantFeatureService] feature flags unavailable — ${context} falls back to legacy view`);
    try {
      datadogRum.addAction("tenant_features_unavailable", {context});
    } catch {
      // RUM is not initialised in every plugin context; the warning above is the fallback.
    }
  }
}
